import React, { useRef, useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { GitCompare } from 'lucide-react';

/**
 * BeforeAfterSlider - interactive drag reveal slider.
 * CriLam-branded label chip on both sides.
 */
const BeforeAfterSlider = ({ beforeSrc, afterSrc, beforeLabel = 'Before', afterLabel = 'With CriLam PUR' }) => {
  const containerRef = useRef(null);
  const [position, setPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const handleMove = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPosition(pct);
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    const onMouseMove = (e) => handleMove(e.clientX);
    const onTouchMove = (e) => e.touches[0] && handleMove(e.touches[0].clientX);
    const onEnd = () => setIsDragging(false);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('touchend', onEnd);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [isDragging, handleMove]);

  return (
    <div
      ref={containerRef}
      className="relative aspect-video w-full rounded-xl overflow-hidden bg-gray-100 select-none cursor-ew-resize shadow-lg"
      onMouseDown={(e) => { setIsDragging(true); handleMove(e.clientX); }}
      onTouchStart={(e) => { setIsDragging(true); if (e.touches[0]) handleMove(e.touches[0].clientX); }}
      data-testid="before-after-slider"
    >
      {/* After (full behind) */}
      <img src={afterSrc} alt={afterLabel} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      {/* Before (clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
      >
        <img src={beforeSrc} alt={beforeLabel} className="w-full h-full object-cover" draggable={false} />
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 px-3 py-1 bg-black/60 backdrop-blur-sm text-white text-xs font-semibold rounded-full">
        {beforeLabel}
      </div>
      <div className="absolute top-4 right-4 px-3 py-1 bg-[#D4AF37] text-[#0A0D0C] text-xs font-bold rounded-full">
        {afterLabel}
      </div>

      {/* Slider Line + Handle */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_12px_rgba(0,0,0,0.6)]"
        style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
      >
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-xl border-2 border-[#D4AF37]"
          animate={{ scale: isDragging ? 1.1 : 1 }}
        >
          <GitCompare className="h-5 w-5 text-[#004534]" />
        </motion.div>
      </div>

      {/* Watermark corner (visual CriLam branding on all screenshots) */}
      <div className="absolute bottom-3 right-4 px-2 py-1 bg-black/50 backdrop-blur-sm rounded text-[10px] text-white/90 font-medium tracking-wide">
        CriLam Decorative · AI Design Studio
      </div>
    </div>
  );
};

export default BeforeAfterSlider;
